import React, { useMemo } from 'react';
import { sanitizeD365RichTextEditor } from 'src/features/one-way-in/utils/htmlTextConverter';
import SideNavigationMenu, { RouterLinks } from 'src/components/Layout/SideNavigationMenu/SideNavigationMenu';
import useTargetPageOptions from '../../register-service-form/hooks/use-target-page-options';
import useDeliveryTypeOptions from '../../register-service-form/hooks/use-delivery-type-options';
import { Button, Dialog, Tag } from '@innovationnorway/cl-react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { CompanySizeCriteria, DeliveryType, ServiceTemplateDto } from 'src/api/v2';
import { SERVICES } from '../../routes';
import { useTranslation } from 'react-i18next';
import useSiteimprove from 'src/hooks/use-siteimprove';
import { SITEIMPROVE_DECLARATIONS } from 'src/features/tracking/constants';

interface ServiceTemplateSelectorProps {
  dialogOpen: boolean;
  setDialogOpen: (open: boolean) => void;
  allServiceTemplates: ServiceTemplateDto[] | undefined;
}

const ServiceTemplateSelector = ({
  dialogOpen,
  setDialogOpen,
  allServiceTemplates,
}: ServiceTemplateSelectorProps) => {
  const { serviceTemplateId } = useParams();
  const { companyAgeOptions, companySizeOptions } = useTargetPageOptions();
  const { deliveryTypeOptions } = useDeliveryTypeOptions();
  const navigate = useNavigate();
  const { t: tServices } = useTranslation('services');
  const { t: tOneWayIn } = useTranslation('oneWayIn');

  const isOpen = dialogOpen || !!serviceTemplateId;

  const activeTemplate = useMemo(() => {
    return allServiceTemplates?.find((template) => template.id === serviceTemplateId);
  }, [allServiceTemplates, serviceTemplateId]);

  const routerLinks: RouterLinks[] = useMemo(() => {
    return (
      allServiceTemplates
        ?.sort((a, b) => a.name.localeCompare(b.name))
        .map((template, index) => ({
          id: index,
          isVisible: true,
          to: `${SERVICES.BASE}/serviceTemplate/${template.id}`,
          textContent: template.name,
          state: { templateId: template.id },
        })) || []
    );
  }, [allServiceTemplates]);

  const siteimprove = useSiteimprove();

  return (
    <Dialog
      open={isOpen}
      onClose={() => {
        navigate(SERVICES.BASE);
        setDialogOpen(false);
      }}
      className="dialog-with-sidebar"
    >
      <div className="stack-small padding-x--3 padding-bottom--1">
        <span className="dialog-with-sidebar--heading">{tServices('addService')}</span>
        <p className="font-size--medium">{tServices('templates.info')}</p>
      </div>
      <div className="dialog-with-sidebar--container service-template--container with-sidebar">
        <SideNavigationMenu
          className="dialog-with-sidebar--side-bar"
          routerLinks={routerLinks}
          collapsable={false}
        />
        <div className="stack-medium padding--2 font-size--medium border-radius--1 background-color--white">
          <div
            className="service-template--truncate"
            dangerouslySetInnerHTML={{
              __html: sanitizeD365RichTextEditor(activeTemplate?.description || ''),
            }}
          ></div>
          <div className="service-template--truncate">
            <span className="font-weight--bold">{tServices('purpose')}:</span>{' '}
            <span
              dangerouslySetInnerHTML={{
                __html: sanitizeD365RichTextEditor(activeTemplate?.servicePurpose || '')
                  .replace('<p>', '<span>')
                  .replace('</p>', '</span>'),
              }}
            />
          </div>
          <div>
            <span className="font-weight--bold">{tOneWayIn('referring.companyAge')}:</span>{' '}
            {companyAgeOptions.find((option) => option.value === activeTemplate?.companyAge)?.label || ''}
          </div>
          <div>
            <span className="font-weight--bold">{tOneWayIn('referring.companySize')}:</span>{' '}
            {companySizeOptions
              .filter((option) => activeTemplate?.companySizes?.includes(option.value as CompanySizeCriteria))
              .map((option) => option.label)
              .join(', ') || ''}
          </div>
          <div className="service-template-delivery-types--container">
            <span className="font-weight--bold">{tServices('details.deliveryType')}:</span>{' '}
            <div className="service-template--delivery-types">
              {deliveryTypeOptions
                .filter((option) => activeTemplate?.deliveryTypes?.includes(option.value as DeliveryType))
                .map((dt) => {
                  return (
                    <Tag key={String(dt)} data-color={'info' as const}>
                      {String(dt.label)}
                    </Tag>
                  );
                })}
            </div>
          </div>
        </div>
      </div>
      <div className="dialog-with-sidebar--action-row">
        <Button
          onClick={() =>
            siteimprove.log(
              SITEIMPROVE_DECLARATIONS.ADD_SERVICE.id,
              SITEIMPROVE_DECLARATIONS.ADD_SERVICE.actions.FROM_TEMPLATE,
              activeTemplate?.name,
            )
          }
          asChild
        >
          <Link className="color--white" to={`${SERVICES.FROM_SERVICE_TEMPLATE}${activeTemplate?.id}`}>
            {tServices('templates.useTemplate')}
          </Link>
        </Button>
        <Button
          onClick={() =>
            siteimprove.log(
              SITEIMPROVE_DECLARATIONS.ADD_SERVICE.id,
              SITEIMPROVE_DECLARATIONS.ADD_SERVICE.actions.EMPTY_FORM,
            )
          }
          asChild
          variant="secondary"
        >
          <Link className="color--in-black-400" to={SERVICES.NEW_SERVICE}>
            {tServices('templates.emptyForm')}
          </Link>
        </Button>
      </div>
    </Dialog>
  );
};

export default ServiceTemplateSelector;
